import { CommandBlock } from "@/components/CommandBlock";
import { useCopyToClipboard } from "@/lib/useCopyToClipboard";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { LoadError } from "@/components/LoadError";
import { Select } from "@/components/Select";
import { SetupStep } from "@/components/SetupStep";
import { useApi } from "@/lib/api";
import { plural } from "@/lib/plural";
import type { PagedResult } from "@/lib/pagination";
import type { AccessibleResource } from "./shared";

const resourceTypes = [
  {
    type: "VirtualMachine",
    label: "Virtual machines",
    path: "virtual-machines",
  },
  { type: "Database", label: "Databases", path: "databases" },
  {
    type: "KubernetesCluster",
    label: "Kubernetes clusters",
    path: "kubernetes-clusters",
  },
];

const platforms = [
  { value: "darwin-arm64", label: "macOS · Apple silicon" },
  { value: "darwin-amd64", label: "macOS · Intel" },
  { value: "windows-amd64.exe", label: "Windows · x64" },
  { value: "windows-arm64.exe", label: "Windows · ARM64" },
  { value: "linux-amd64", label: "Linux · x64" },
  { value: "linux-arm64", label: "Linux · ARM64" },
];

export function MemberHome() {
  const { copiedField, copyToClipboard } = useCopyToClipboard();
  const { user } = useAuth();
  const { orgId } = useParams();
  const api = useApi();
  const [platform, setPlatform] = useState(platforms[0]!.value);
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<{
    orgId: string | undefined;
    counts: number[] | null;
  } | null>(null);
  const current = result?.orgId === orgId ? result : null;
  const counts = current?.counts;
  const total = counts?.reduce((sum, count) => sum + count, 0);
  const firstName = user?.name?.trim().split(/\s+/)[0];
  const base = `/${orgId}/resources`;
  const windows = platform.startsWith("windows");
  const executable = windows ? ".\\t4a.exe" : "./t4a";

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const responses = await Promise.all(
        resourceTypes.map(({ type }) =>
          api.get<PagedResult<AccessibleResource>>(
            `/organizations/${orgId}/resources?type=${type}&page=1&pageSize=1`,
          ),
        ),
      );
      if (!cancelled)
        setResult({
          orgId,
          counts: responses.every((response) => response !== null)
            ? responses.map((response) => response!.totalCount)
            : null,
        });
    }
    void load();
    const interval = setInterval(() => void load(), 30_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [api, orgId, attempt]);

  return (
    <>
      <div className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">
          Get connected{firstName ? `, ${firstName}` : ""}
        </h1>
        <p className="mt-0.5 text-sm text-foreground/70">
          {total === undefined
            ? "Set up the Tendra client to connect to your resources."
            : total === 0
              ? "Install the client while your administrator grants you access."
              : `You have access to ${plural(total, "resource")}. Let's get you connected!`}
        </p>
      </div>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <section
          className="rounded-lg border border-muted bg-surface/40 p-6"
          aria-labelledby="connection-steps"
        >
          <h2 id="connection-steps" className="mb-6 text-sm font-semibold">
            Connect in a few simple steps
          </h2>
          <ol>
            <SetupStep
              label="1"
              state="current"
              title="Download the Tendra client"
            >
              <p className="mt-0.5 text-sm text-foreground/70">
                Download the Tendra client, rename it to{" "}
                <code className="text-foreground">
                  {windows ? "t4a.exe" : "t4a"}
                </code>
                , then open a terminal in the same folder. Make the client
                executable with{" "}
                <code className="text-foreground">chmod +x t4a</code>.
              </p>
              <div className="my-3 flex flex-wrap items-center gap-2">
                <Select
                  aria-label="Client platform"
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                >
                  {platforms.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </Select>
                <a
                  className="inline-flex h-10 items-center rounded-lg bg-accent px-4 text-sm text-surface hover:bg-accent/80"
                  href={`https://get.tendra.sh/client/latest/t4a-${platform}`}
                >
                  Download client
                </a>
              </div>
            </SetupStep>

            <SetupStep label="2" state="current" title="Sign in">
              <p className="mt-0.5 text-sm text-foreground/70">
                Run this command, then sign in with your Tendra account.
              </p>
              <CommandBlock
                command={`${executable} login`}
                field="loginCommand"
                copiedField={copiedField}
                onCopy={copyToClipboard}
                className="mt-3"
              />
            </SetupStep>

            <SetupStep
              label="3"
              state="current"
              title="Connect to a resource"
              last
            >
              <p className="mt-0.5 text-sm text-foreground/70">
                List your resources, then connect to one using its name or ID.
              </p>
              <CommandBlock
                command={`${executable} list\n${executable} connect <resource-name-or-id>`}
                field="connectCommand"
                copiedField={copiedField}
                onCopy={copyToClipboard}
                className="mt-3"
              />
            </SetupStep>
          </ol>
        </section>
        <section aria-labelledby="available-resources">
          <h2 id="available-resources" className="mb-3 text-sm font-semibold">
            Resources available to you
          </h2>
          {current && !counts ? (
            <LoadError
              what="your resources"
              onRetry={() => {
                setResult(null);
                setAttempt((value) => value + 1);
              }}
            />
          ) : (
            <ul>
              {resourceTypes.map((item, index) => (
                <li key={item.type} className="border-b border-muted/60">
                  <Link
                    to={`${base}/${item.path}`}
                    className="flex items-center justify-between gap-3 rounded py-4 text-sm hover:text-accent"
                  >
                    <span>{item.label}</span>
                    {counts ? (
                      <span className="tabular-nums">{counts[index]}</span>
                    ) : (
                      <span
                        role="status"
                        aria-label="Loading"
                        className="h-3 w-6 animate-pulse rounded bg-surface"
                      />
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <Link
            className="mt-4 inline-block text-sm text-accent hover:underline"
            to={base}
          >
            View your resources →
          </Link>
          <div className="mt-8 border-l-2 border-accent pl-4">
            <h3 className="text-sm font-semibold">Missing something?</h3>
            <p className="mt-0.5 text-sm text-foreground/70">
              Your team memberships determine which resources you can access.
              Contact your administrator if a resource you need isn't listed.
            </p>
          </div>
        </section>
      </div>
    </>
  );
}
